import { chapterDetailsActionTypes } from './Constants';
import * as chapterDetailsActions from './Actions';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { processRequest } from '../../services/Api';
import { relatedQuestionsData } from './Selectors';
import * as errorHandlerActions from '../../components/errorHandler/Actions';

export default function* () {
	yield all([
		takeLatest(chapterDetailsActionTypes.GET_CHAPTER_DETAILS, handleChapterDetailsRequest),
		takeLatest(
			chapterDetailsActionTypes.GET_CHAPTER_DETAILS_PARAGRAPHS,
			handleChapterDetailsParagraphsRequest,
		),
		takeLatest(
			chapterDetailsActionTypes.GET_CHAPTER_DETAILS_SUMMARY,
			handleChapterDetailsSummaryRequest,
		),
		takeLatest(
			chapterDetailsActionTypes.GET_PAUSED_DIAGNOSTIC_TEST,
			handlePausedDiagnosticTestRequest,
		),
		takeLatest(chapterDetailsActionTypes.GET_TO_DO_TOPICS, handleToDoTopicsRequest),
		takeLatest(
			chapterDetailsActionTypes.GET_CHAPTER_RELATED_QUESTIONS,
			handleGetRelatedQuestionsRequest,
		),
	]);
}

export function* handleChapterDetailsRequest({ payload }) {
	try {
		const { chapterId } = payload || {};

		const { data } = yield call(processRequest, `public/chapters/${chapterId}`);
		yield put(
			chapterDetailsActions.getChapterDetailsSuccess({
				...data.chapter,
				no_index: data.no_index,
			}),
		);
	} catch (e) {
		yield put(errorHandlerActions.handleError(e));
		yield put(chapterDetailsActions.getChapterDetailsError());
	}
}

export function* handleChapterDetailsParagraphsRequest({ payload }) {
	try {
		const { chapterId } = payload || {};
		const { data } = yield call(processRequest, `public/chapters/${chapterId}/paragraphs`);

		yield put(chapterDetailsActions.getChapterDetailsParagraphsSuccess(data.paragraphs));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(chapterDetailsActions.getChapterDetailsParagraphsError());
	}
}

export function* handleChapterDetailsSummaryRequest({ payload }) {
	try {
		const { chapterId, videokey } = payload || {};
		const { data } = yield call(processRequest, `public/chapters/${chapterId}/summary`, 'GET', {
			videokey,
		});

		yield put(chapterDetailsActions.getChapterDetailsSummarySuccess(data.summary));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(chapterDetailsActions.getChapterDetailsSummaryError());
	}
}

export function* handlePausedDiagnosticTestRequest({ payload }) {
	try {
		const { chapterId, isPublic, loadParagraphs, exerciseId } = payload || {};
		const diagnosticTestData = {};

		if (loadParagraphs) {
			const { data: paragraphsData } = yield call(
				processRequest,
				`public/chapters/${chapterId}/paragraphs`,
			);
			diagnosticTestData.chapterDetailsParagraphs = paragraphsData.paragraphs;
		}

		if (isPublic) {
			yield put(chapterDetailsActions.getPausedDiagnosticTestSuccess(diagnosticTestData));
			return;
		}

		const { data } = yield call(
			processRequest,
			`chapters/${chapterId}/exercises/paused_diagnostic_test`,
		);

		diagnosticTestData.pausedDiagnosticTest = data.paused_diagnostic_test;

		if (exerciseId) {
			const { data: toDoTopicsData } = yield call(
				processRequest,
				`exercises/${exerciseId}/to_do_topics`,
			);
			diagnosticTestData.toDoTopics = toDoTopicsData.to_do_topics;
		}

		yield put(chapterDetailsActions.getPausedDiagnosticTestSuccess(diagnosticTestData));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(chapterDetailsActions.getPausedDiagnosticTestError());
	}
}

export function* handleToDoTopicsRequest({ payload }) {
	try {
		const { exerciseId } = payload || {};
		const { data } = yield call(processRequest, `exercises/${exerciseId}/to_do_topics`);

		yield put(chapterDetailsActions.getToDoTopicsSuccess(data.to_do_topics));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'content-blocking'));
		yield put(chapterDetailsActions.getToDoTopicsError());
	}
}

export function* handleGetRelatedQuestionsRequest({ payload }) {
	try {
		const { data: queries, chapterId } = payload || {};

		const { data } = yield call(
			processRequest,
			`public/chapters/${chapterId}/qna_questions`,
			'GET',
			queries,
		);

		const formattedData = { results: data.qna_questions, total_count: data.total_count };

		if (queries && queries.offset) {
			const currentData = yield select(relatedQuestionsData);
			formattedData.results = [...currentData.results, ...data.qna_questions];
		}

		yield put(chapterDetailsActions.getRelatedQuestionsSuccess(formattedData));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(chapterDetailsActions.getRelatedQuestionsError());
	}
}
